import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Database, Search, X } from 'lucide-react';
import { fieldClass, ghostBtn, primaryBtn } from '../components/GatewayUi';
import { logGovernedMcp } from '../governedMcpLogger';
import type { WorkspacePublishAsset } from '../governedMcpTypes';

function rowClass(isLight: boolean, selected: boolean): string {
  if (selected) {
    return isLight
      ? 'w-full text-left px-3 py-2.5 text-sm bg-indigo-50 text-indigo-700'
      : 'w-full text-left px-3 py-2.5 text-sm bg-indigo-500/15 text-indigo-200';
  }
  return isLight
    ? 'w-full text-left px-3 py-2.5 text-sm text-neutral-700 hover:bg-neutral-50'
    : 'w-full text-left px-3 py-2.5 text-sm text-neutral-200 hover:bg-neutral-900';
}

export default function ModelAssetPickerModal({
  open,
  assets,
  selectedIds,
  modelName,
  isLight,
  onClose,
  onApply,
}: {
  open: boolean;
  assets: WorkspacePublishAsset[];
  selectedIds: string[];
  modelName: string;
  isLight: boolean;
  onClose: () => void;
  onApply: (ids: string[]) => void;
}) {
  const [query, setQuery] = useState('');
  const [sourceId, setSourceId] = useState('');
  const [kind, setKind] = useState('');
  const [draftIds, setDraftIds] = useState<string[]>(selectedIds);
  const selectedKey = selectedIds.join(',');

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setSourceId('');
    setKind('');
    setDraftIds(selectedKey ? selectedKey.split(',') : []);
    logGovernedMcp('app', 'publish_model_asset_picker_open', {
      modelName,
      assets: assets.length,
      selected: selectedKey ? selectedKey.split(',').length : 0,
    });
  }, [open, modelName, assets.length, selectedKey]);

  const sources = useMemo(() => {
    const seen = new Map<string, string>();
    assets.forEach((asset) => {
      if (asset.sourceId && !seen.has(asset.sourceId)) {
        seen.set(asset.sourceId, asset.sourceName || 'Source');
      }
    });
    return Array.from(seen, ([id, name]) => ({ id, name }));
  }, [assets]);

  const kinds = useMemo(() => {
    return Array.from(
      new Set(assets.map((asset) => (asset.assetKind || 'Table').trim()).filter(Boolean)),
    ).sort((left, right) => left.localeCompare(right));
  }, [assets]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return assets.filter((asset) => {
      if (sourceId && asset.sourceId !== sourceId) return false;
      if (kind && (asset.assetKind || 'Table').toLowerCase() !== kind.toLowerCase()) return false;
      if (!q) return true;
      return `${asset.name} ${asset.sourceName} ${asset.assetKind} ${asset.path}`.toLowerCase().includes(q);
    });
  }, [assets, kind, query, sourceId]);

  const visibleIds = visible.map((asset) => asset.id);
  const selectedVisible = visibleIds.filter((id) => draftIds.includes(id)).length;
  const allVisibleOn = visible.length > 0 && selectedVisible === visible.length;

  if (!open) return null;

  const muted = isLight ? 'text-neutral-500' : 'text-neutral-400';
  const panel = isLight ? 'bg-white text-neutral-900' : 'bg-neutral-900 text-white';
  const rail = isLight ? 'border-neutral-200 bg-neutral-50' : 'border-neutral-800 bg-neutral-950';

  const toggle = (assetId: string) => {
    setDraftIds((current) =>
      current.includes(assetId) ? current.filter((id) => id !== assetId) : [...current, assetId],
    );
  };

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <button type="button" className="absolute inset-0 bg-black/40" aria-label="Close asset picker" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="model-asset-picker-title"
        className={`relative flex flex-col w-full max-w-2xl max-h-[min(40rem,calc(100dvh-2rem))] overflow-hidden rounded-2xl shadow-2xl ${panel}`}
      >
        <div className="shrink-0 flex items-center justify-between gap-3 px-5 py-4 border-b border-black/5">
          <div>
            <h3 id="model-asset-picker-title" className="text-base font-semibold">Select assets</h3>
            <p className={`text-xs mt-0.5 ${muted}`}>
              {modelName || 'Data model'} · filter and choose tables this tool can query
            </p>
          </div>
          <button type="button" className={ghostBtn(isLight)} onClick={onClose}>
            <X size={16} />
          </button>
        </div>
        <div className={`shrink-0 grid gap-2 px-5 py-4 border-b ${isLight ? 'border-neutral-100' : 'border-neutral-800'} sm:grid-cols-[1fr_auto_auto]`}>
          <div className="relative min-w-0">
            <Search size={15} className={`absolute left-3 top-1/2 -translate-y-1/2 ${muted}`} />
            <input
              className={`${fieldClass(isLight)} pl-9`}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Filter by name, source, or path"
            />
          </div>
          {sources.length > 1 ? (
            <select className={fieldClass(isLight)} value={sourceId} onChange={(event) => setSourceId(event.target.value)}>
              <option value="">All sources</option>
              {sources.map((source) => (
                <option key={source.id} value={source.id}>
                  {source.name}
                </option>
              ))}
            </select>
          ) : null}
          {kinds.length > 1 ? (
            <select className={fieldClass(isLight)} value={kind} onChange={(event) => setKind(event.target.value)}>
              <option value="">All kinds</option>
              {kinds.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          ) : null}
        </div>
        <div className={`shrink-0 flex items-center justify-between gap-2 px-5 py-2 text-xs ${rail}`}>
          <p className={muted}>
            {selectedVisible} of {visible.length} shown · {draftIds.length} selected
          </p>
          <button
            type="button"
            className={isLight ? 'text-indigo-600' : 'text-indigo-300'}
            disabled={!visible.length}
            onClick={() => {
              setDraftIds((current) =>
                allVisibleOn
                  ? current.filter((id) => !visibleIds.includes(id))
                  : Array.from(new Set([...current, ...visibleIds])),
              );
            }}
          >
            {allVisibleOn ? 'Clear shown' : 'Select shown'}
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {visible.map((asset) => {
            const checked = draftIds.includes(asset.id);
            return (
              <button
                key={asset.id}
                type="button"
                className={rowClass(isLight, checked)}
                onClick={() => toggle(asset.id)}
              >
                <span className="flex items-center gap-2.5">
                  <span
                    className={`flex h-4 w-4 items-center justify-center rounded border ${
                      checked
                        ? 'border-indigo-500 bg-indigo-500 text-white'
                        : isLight
                          ? 'border-neutral-300 bg-white'
                          : 'border-neutral-600 bg-neutral-900'
                    }`}
                  >
                    {checked ? <Check size={11} /> : null}
                  </span>
                  <Database size={14} className="shrink-0" />
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{asset.name}</span>
                    <span className={`block truncate text-[11px] ${muted}`}>
                      {asset.sourceName || 'Source'} · {asset.assetKind || 'Table'}
                      {asset.path ? ` · ${asset.path}` : ''}
                    </span>
                  </span>
                </span>
              </button>
            );
          })}
          {!visible.length ? (
            <p className={`px-5 py-10 text-sm text-center ${muted}`}>
              {assets.length ? 'No assets match this filter.' : 'This data model has no table or view assets.'}
            </p>
          ) : null}
        </div>
        <div className={`shrink-0 flex items-center justify-end gap-2 px-5 py-4 border-t ${isLight ? 'border-neutral-100' : 'border-neutral-800'}`}>
          <button type="button" className={ghostBtn(isLight)} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={primaryBtn()}
            onClick={() => {
              onApply(draftIds);
              logGovernedMcp('app', 'publish_model_assets_applied', { count: draftIds.length });
            }}
          >
            <Check size={14} /> Use {draftIds.length} asset{draftIds.length === 1 ? '' : 's'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
